import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import isHotKey from 'is-hotkey';
import debounce from 'lodash/debounce';
import { Editor as SlateEditor } from 'slate-react';
import SlatePlainSerializer from 'slate-plain-serializer';

var defaultRenderBlock = function defaultRenderBlock(_ref) {
  var node = _ref.node,
      attributes = _ref.attributes,
      children = _ref.children;
  return React.createElement("div", Object.assign({
    "data-type": node.type
  }, attributes), children);
};

var defaultRenderInline = function defaultRenderInline(_ref2) {
  var node = _ref2.node,
      attributes = _ref2.attributes,
      children = _ref2.children;
  return React.createElement("span", Object.assign({
    "data-type": node.type
  }, attributes), children);
};

var renderPlaceholder = function renderPlaceholder(placeholder) {
  return function (props, editor, next) {
    var children = props.children,
        decoration = props.decoration;

    if (decoration.type === 'placeholder') {
      return React.createElement("span", Object.assign({
        "data-placeholder": placeholder
      }, props.attributes), children);
    }

    return next();
  };
};

function convertToText(value) {
  return SlatePlainSerializer.serialize(value);
}

function converFromText() {
  var text = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

  try {
    return SlatePlainSerializer.deserialize(text);
  } catch (error) {
    return SlatePlainSerializer.deserialize('');
  }
}

function InputTransient(_ref3) {
  var value = _ref3.value,
      _ref3$multiline = _ref3.multiline,
      multiline = _ref3$multiline === void 0 ? false : _ref3$multiline,
      _ref3$placeholder = _ref3.placeholder,
      placeholder = _ref3$placeholder === void 0 ? 'Untitled' : _ref3$placeholder,
      _ref3$onEnter = _ref3.onEnter,
      onEnter = _ref3$onEnter === void 0 ? function (event) {} : _ref3$onEnter,
      _ref3$onClick = _ref3.onClick,
      onClick = _ref3$onClick === void 0 ? function (event) {} : _ref3$onClick,
      onChange = _ref3.onChange,
      _ref3$onBlur = _ref3.onBlur,
      onBlur = _ref3$onBlur === void 0 ? function (event) {} : _ref3$onBlur,
      onKeyDown = _ref3.onKeyDown,
      _ref3$plugins = _ref3.plugins,
      plugins = _ref3$plugins === void 0 ? [] : _ref3$plugins,
      _ref3$autoFocus = _ref3.autoFocus,
      autoFocus = _ref3$autoFocus === void 0 ? false : _ref3$autoFocus,
      _ref3$autoSelect = _ref3.autoSelect,
      autoSelect = _ref3$autoSelect === void 0 ? false : _ref3$autoSelect,
      _ref3$debounceTimeout = _ref3.debounceTimeout,
      debounceTimeout = _ref3$debounceTimeout === void 0 ? 300 : _ref3$debounceTimeout,
      props = _objectWithoutProperties(_ref3, ["value", "multiline", "placeholder", "onEnter", "onClick", "onChange", "onBlur", "onKeyDown", "plugins", "autoFocus", "autoSelect", "debounceTimeout"]);

  var _React$useState = React.useState(),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      editor = _React$useState2[0],
      setEditor = _React$useState2[1];

  var _React$useState3 = React.useState(function initState() {
    return converFromText(value);
  }),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      editorValue = _React$useState4[0],
      setEditorValue = _React$useState4[1]; // We use Ref instead of State because of Slate memoizing the Event Hanlders
  // https://github.com/ianstormtaylor/slate/issues/2927


  var dirty = React.useRef(false);

  var _React$useState5 = React.useState(function () {
    return [{
      renderBlock: defaultRenderBlock,
      renderInline: defaultRenderInline,
      renderDecoration: renderPlaceholder(placeholder),
      schema: !multiline ? {
        document: {
          nodes: [{
            min: 1,
            max: 1
          }]
        }
      } : {}
    }].concat(plugins);
  }),
      _React$useState6 = _slicedToArray(_React$useState5, 1),
      editorPlugins = _React$useState6[0];

  var focusEnd = React.useCallback(function () {
    if (!editor) return;
    editor.focus().moveToEndOfDocument();
  }, [editor]);
  React.useEffect(function () {
    if (autoFocus) {
      focusEnd();
    }
  }, [autoFocus, focusEnd]);
  React.useEffect(function enforceFocus() {
    if (!editor || !autoFocus) {
      return;
    }

    if (!editorValue.selection.isFocused) {
      editor.moveToEndOfDocument();
    }

    if (autoFocus) {
      editor.focus();
      var changedText = convertToText(editorValue);

      if (changedText) {
        onChange(changedText);
      }
    }
  }, [editor, autoFocus, editorValue, onChange]);
  React.useEffect(function enforceSelect() {
    if (!editor || !autoSelect) {
      return;
    }

    if (!editorValue.selection.isFocused) {
      editor.moveToRangeOfDocument();
    }

    if (autoSelect) {
      editor.focus();
    }
  }, [editor, autoSelect, editorValue]);
  var onEditorClick = React.useCallback(function onEditorClick(event) {
    if (onClick) {
      onClick(event);
    }
  }, [onClick]);
  var debounceValueChange = React.useCallback(debounce(function (val) {
    return val !== value && onChange(val);
  }, debounceTimeout), [value, debounce, onChange]);
  var onEditorChange = React.useCallback(function (change) {
    var newValue = convertToText(change.value);

    if (!dirty.current && value !== newValue) {
      dirty.current = true;
    }

    setEditorValue(change.value);
    debounceValueChange(newValue);
  }, [value, setEditorValue, debounceValueChange]);
  var onSubmit = React.useCallback(function (value) {
    if (!dirty.current) return;
    debounceValueChange.cancel();
    onChange(convertToText(value));
    dirty.current = false;
  }, [onChange, debounceValueChange]);
  var onEditorEnter = React.useCallback(function (event, editor) {
    onSubmit(editor.value);
    onEnter(event);
  }, [onSubmit, onEnter]);
  var onEditorBlur = React.useCallback(function (event, editor) {
    onSubmit(editor.value);
    onBlur(event);
  }, [onBlur, onSubmit]);
  var onEditorKeyDown = React.useCallback(function (event, editor, next) {
    if (isHotKey('enter', event)) {
      event.preventDefault();
      event.stopPropagation();
      return onEditorEnter(event, editor);
    }

    if (onKeyDown) {
      return onKeyDown(event, next);
    }

    if (isHotKey('tab', event)) {
      onEditorBlur(event, editor);
    }

    return next();
  }, [onEditorEnter, onKeyDown, onEditorBlur]);
  React.useEffect(function () {
    if (dirty.current) return;
    setEditorValue(converFromText(value));
  }, [dirty, value, setEditorValue, autoFocus, autoSelect]);
  React.useEffect( //OnBlur on slate editor cant be used since readOnly takes precedence ie. if readOnly goes to true, onBlur won't be called
  function () {
    if (props.readOnly && editor) {
      onSubmit(editor.value);
      onBlur();
    }
  }, [onSubmit, onBlur, editor, props.readOnly]);
  return React.createElement(SlateEditor, Object.assign({}, props, {
    ref: setEditor,
    plugins: editorPlugins,
    onClick: onEditorClick,
    onKeyDown: onEditorKeyDown,
    onChange: onEditorChange,
    onBlur: onEditorBlur,
    value: editorValue
  }));
}

InputTransient.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  debounceTimeout: PropTypes.number,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onEnter: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  autoSelect: PropTypes.bool,
  readOnly: PropTypes.bool,
  plugins: PropTypes.array,
  autoFocus: PropTypes.bool,
  multiline: PropTypes.bool
};
export default React.memo(InputTransient);